/**
 * <AssignTaskDialog> — hand work to somebody, or to yourself.
 *
 * The human-authored twin of a declared work rule: the same task, in the same
 * index, opened by the same click. A rule says "whenever a purchase order is
 * approved, stores must receive it"; this says "Ravi, receive this one". The
 * product needs both, because no rule anticipates a phone call.
 *
 * Two entry points, one dialog:
 *  - from the work panel, where it is a plain to-do ("Call the supplier back")
 *  - from a record, where it carries that record, so the assignee opens the
 *    thing rather than hunting for it — this is the difference between a task
 *    and a reminder to go and find something.
 *
 * Assigning to a ROLE is a first-class choice, not an afterthought: most work
 * in an application is owed by whoever is on the desk, and forcing the sender
 * to name a person is how a queue ends up on somebody's holiday.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Check, Loader2, Search, UserPlus, Users, X } from 'lucide-react'

import type {
  NewTaskInput, Principal, PrincipalDirectory, WorkTask, WorklistRuntime,
} from './contract'

/**
 * The three calls this dialog makes. Narrowed deliberately: a host that only
 * wants an assign dialog (a transaction screen, say) should not have to hand
 * over an approvals decider and a document resolver to get one.
 */
export type AssignRuntime = Pick<WorklistRuntime, 'create' | 'principals' | 'notify'>

export interface AssignTaskDialogProps {
  runtime: AssignRuntime
  onClose: () => void
  /** Called with the created task, so the caller can refresh its badge. */
  onCreated?: (task: WorkTask) => void
  /**
   * The record this is about, when the dialog was opened from one. Carrying it
   * makes the task OPEN that record; without it the task is a plain to-do,
   * which is the right shape for "ring the supplier".
   */
  record?: {
    objName: string
    recordKey: string
    /** How the record reads — the same label the rest of the app shows. */
    label?: string
    /** What the screen is called, for the default title. */
    objTitle?: string
  }
}

/** Local midnight → the ISO instant the server stores. */
function dueIso(value: string): string | null {
  if (!value) return null
  const d = new Date(`${value}T17:00:00`)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

export default function AssignTaskDialog({
  runtime, onClose, onCreated, record,
}: AssignTaskDialogProps) {
  const [dir, setDir] = useState<PrincipalDirectory | null>(null)
  const [dirError, setDirError] = useState('')
  const [query, setQuery] = useState('')
  const [picked, setPicked] = useState<Principal[]>([])
  const [title, setTitle] = useState(record
    ? `${record.objTitle || record.objName}: ${record.label || record.recordKey}`
    : '')
  const [verb, setVerb] = useState(record ? 'Open' : '')
  const [summary, setSummary] = useState('')
  const [due, setDue] = useState('')
  const [priority, setPriority] = useState('normal')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const alive = useRef(true)

  useEffect(() => () => { alive.current = false }, [])

  useEffect(() => {
    if (!runtime.principals) { setDirError('This app cannot list its people.'); return undefined }
    let live = true
    void (async () => {
      try {
        const res = await runtime.principals!()
        if (live) setDir(res)
      } catch (err) {
        if (live) {
          setDirError(err instanceof Error ? err.message
            : 'Could not load the people in this app.')
        }
      }
    })()
    return () => { live = false }
  }, [runtime])

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    const users = dir?.users || []
    const roles = dir?.roles || []
    const hit = (p: Principal) => !q
      || p.name.toLowerCase().includes(q)
      || String(p.email || '').toLowerCase().includes(q)
      || p.id.toLowerCase().includes(q)
    // Roles first when the query looks like one: a person searching "finance"
    // usually means the desk, not the one colleague whose surname matches.
    return [...roles.filter(hit), ...users.filter(hit)].slice(0, 40)
  }, [dir, query])

  const toggle = useCallback((p: Principal) => {
    setPicked((cur) => (cur.some((c) => c.kind === p.kind && c.id === p.id)
      ? cur.filter((c) => !(c.kind === p.kind && c.id === p.id))
      : [...cur, p]))
  }, [])

  const submit = useCallback(async () => {
    const name = title.trim()
    if (!name) { setError('Say what needs doing.'); return }
    if (picked.length === 0) { setError('Choose who this is for.'); return }
    setBusy(true)
    setError('')
    try {
      const input: NewTaskInput = {
        title: name,
        assignees: picked.map((p) => ({ kind: p.kind, id: p.id })),
        summary: summary.trim() || undefined,
        verb: verb.trim() || undefined,
        priority,
        due_at: dueIso(due),
        ...(record ? {
          obj_name: record.objName,
          record_key: record.recordKey,
          // The assignee opens the record itself. `open: 'record'` is the
          // same target a work rule writes, so both kinds of task travel
          // through one opener in every host.
          target: {
            open: 'record' as const,
            obj_name: record.objName,
            verb: verb.trim() || 'Open',
          },
        } : {}),
      }
      const task = await runtime.create(input)
      runtime.notify?.(picked.length === 1
        ? `Assigned to ${picked[0].name}.`
        : `Assigned to ${picked.length} people.`, 'success')
      onCreated?.(task)
      onClose()
    } catch (err) {
      // Say why, in place. A dialog that closes on failure loses everything
      // the sender typed, and they will not type it twice.
      setError(err instanceof Error ? err.message : 'Could not create that task.')
    } finally {
      if (alive.current) setBusy(false)
    }
  }, [title, picked, summary, verb, priority, due, record, runtime, onCreated, onClose])

  return (
    <div
      role="dialog"
      aria-label="Assign work"
      onClick={onClose}
      style={{
        // Above the work panel (9430), which is where it is opened from.
        position: 'fixed', inset: 0, zIndex: 9460,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(15,23,42,0.45)', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(560px, 100%)', maxHeight: '92vh',
          display: 'flex', flexDirection: 'column',
          borderRadius: 14, overflow: 'hidden',
          background: 'var(--color-bg, #fff)',
          border: '1px solid var(--color-border, #e2e8f0)',
          boxShadow: '0 24px 64px rgba(15,23,42,0.28)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '0.9rem 1rem',
          borderBottom: '1px solid var(--color-border, #e2e8f0)',
        }}>
          <UserPlus size={17} />
          <strong style={{ fontSize: '1.0125rem' }}>
            {record ? 'Assign this record' : 'New task'}
          </strong>
          <span style={{ flex: 1 }} />
          <button type="button" onClick={onClose} title="Close" style={iconBtn}>
            <X size={16} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0.9rem 1rem' }}>
          {record && (
            <div style={{
              fontSize: '0.8425rem', marginBottom: 12, padding: '0.5rem 0.7rem',
              borderRadius: 8, background: 'var(--color-bg-secondary, #f8fafc)',
              border: '1px solid var(--color-border, #e2e8f0)',
              color: 'var(--color-text-secondary, #64748b)',
            }}>
              About <strong style={{ color: 'var(--color-text, #0f172a)' }}>
                {record.label || record.recordKey}
              </strong>{' '}in {record.objTitle || record.objName}. Whoever you
              choose opens this record straight from their work list.
            </div>
          )}

          <Field label="What needs doing" hint="This is the line they will read in their list.">
            <input
              value={title} autoFocus
              onChange={(e) => setTitle(e.target.value)}
              placeholder={record ? 'Check and confirm' : 'e.g. Call ABC Supplies about the short delivery'}
              style={input}
            />
          </Field>

          <Field label="Who it is for" hint="A role hands it to whoever is on that desk; a person hands it to them.">
            {dirError ? (
              <div style={{ fontSize: '0.8425rem', color: 'var(--color-error, #b91c1c)' }}>
                {dirError}
              </div>
            ) : (
              <>
                {picked.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                    {picked.map((p) => (
                      <span
                        key={`${p.kind}:${p.id}`}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '2px 6px 2px 9px', borderRadius: 999,
                          fontSize: '0.8125rem', fontWeight: 600,
                          border: '1px solid var(--color-accent, #4c8dff)',
                          color: 'var(--color-accent, #4c8dff)',
                        }}
                      >
                        {p.kind === 'role' && <Users size={11} />}
                        {p.name}
                        <button
                          type="button" onClick={() => toggle(p)} title="Remove"
                          style={{
                            border: 'none', background: 'transparent', cursor: 'pointer',
                            color: 'inherit', display: 'inline-flex', padding: 2,
                          }}
                        >
                          <X size={11} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div style={{ position: 'relative' }}>
                  <Search
                    size={14}
                    style={{
                      position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)',
                      opacity: 0.5, pointerEvents: 'none',
                    }}
                  />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search people and roles"
                    style={{ ...input, paddingLeft: 28 }}
                  />
                </div>
                <div style={{
                  marginTop: 6, maxHeight: 190, overflowY: 'auto',
                  border: '1px solid var(--color-border, #e2e8f0)', borderRadius: 8,
                }}>
                  {!dir && <div style={listNote}><Loader2 size={13} className="spin" /> Loading…</div>}
                  {dir && matches.length === 0 && (
                    <div style={listNote}>Nobody matches that.</div>
                  )}
                  {matches.map((p) => {
                    const on = picked.some((c) => c.kind === p.kind && c.id === p.id)
                    return (
                      <button
                        key={`${p.kind}:${p.id}`}
                        type="button"
                        onClick={() => toggle(p)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                          padding: '0.4rem 0.6rem', border: 'none', cursor: 'pointer',
                          textAlign: 'left', fontSize: '0.8625rem',
                          background: on
                            ? 'var(--color-accent-weak, rgba(76,141,255,0.12))'
                            : 'transparent',
                          color: 'var(--color-text, #0f172a)',
                        }}
                      >
                        {p.kind === 'role'
                          ? <Users size={13} style={{ opacity: 0.7 }} />
                          : <span style={{
                            width: 13, textAlign: 'center', fontSize: '0.8125rem',
                            opacity: 0.7,
                          }}>@</span>}
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ fontWeight: 600 }}>{p.name}</span>
                          {p.me && <span style={{ opacity: 0.6 }}> · you</span>}
                          {p.kind === 'role' && <span style={{ opacity: 0.6 }}> · role</span>}
                          {p.kind === 'user' && p.roles && p.roles.length > 0 && (
                            <span style={{ opacity: 0.6 }}> · {p.roles.join(', ')}</span>
                          )}
                        </span>
                        {on && <Check size={13} />}
                      </button>
                    )
                  })}
                </div>
              </>
            )}
          </Field>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Field label="Due" hint="Drives the overdue flag." style={{ flex: '1 1 150px' }}>
              <input type="date" value={due} onChange={(e) => setDue(e.target.value)} style={input} />
            </Field>
            <Field label="Priority" style={{ flex: '1 1 130px' }}>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} style={input}>
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
              </select>
            </Field>
            {record && (
              <Field label="Button says" hint="The verb on their row." style={{ flex: '1 1 150px' }}>
                <input value={verb} onChange={(e) => setVerb(e.target.value)} style={input} />
              </Field>
            )}
          </div>

          <Field label="Anything else" hint="Optional — shown under the title.">
            <textarea
              value={summary} rows={2}
              onChange={(e) => setSummary(e.target.value)}
              style={{ ...input, resize: 'vertical' }}
            />
          </Field>

          {error && (
            <div style={{
              fontSize: '0.8425rem', color: 'var(--color-error, #b91c1c)', marginTop: 4,
            }}>{error}</div>
          )}
        </div>

        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '0.75rem 1rem',
          borderTop: '1px solid var(--color-border, #e2e8f0)',
        }}>
          <button type="button" onClick={onClose} style={ghostBtn}>Cancel</button>
          <button
            type="button" onClick={() => { void submit() }} disabled={busy}
            style={{ ...primaryBtn, opacity: busy ? 0.6 : 1 }}
          >
            {busy ? 'Assigning…' : 'Assign'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, hint, children, style }: {
  label: string; hint?: string; children: React.ReactNode
  style?: React.CSSProperties
}) {
  return (
    <label style={{ display: 'block', marginBottom: 12, ...style }}>
      <span style={{
        display: 'block', fontSize: '0.8125rem', fontWeight: 700, marginBottom: 3,
        color: 'var(--color-text-secondary, #475569)',
      }}>{label}</span>
      {children}
      {hint && (
        <span style={{
          display: 'block', fontSize: '0.8125rem', marginTop: 3,
          color: 'var(--color-text-secondary, #94a3b8)',
        }}>{hint}</span>
      )}
    </label>
  )
}

const input: React.CSSProperties = {
  width: '100%', padding: '0.42rem 0.6rem', borderRadius: 8,
  fontSize: '0.8925rem', fontFamily: 'inherit',
  border: '1px solid var(--color-border, #e2e8f0)',
  background: 'var(--color-bg, #fff)', color: 'var(--color-text, #0f172a)',
}

const listNote: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '0.6rem',
  fontSize: '0.8425rem', color: 'var(--color-text-secondary, #64748b)',
}

const iconBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28, borderRadius: 8, cursor: 'pointer',
  border: '1px solid var(--color-border, #e2e8f0)',
  background: 'transparent', color: 'var(--color-text-secondary, #475569)',
}

const ghostBtn: React.CSSProperties = {
  padding: '0.4rem 0.9rem', borderRadius: 8, cursor: 'pointer',
  fontSize: '0.8625rem', fontWeight: 600,
  border: '1px solid var(--color-border, #e2e8f0)',
  background: 'transparent', color: 'var(--color-text-secondary, #475569)',
}

const primaryBtn: React.CSSProperties = {
  padding: '0.4rem 1rem', borderRadius: 8, cursor: 'pointer',
  fontSize: '0.8625rem', fontWeight: 700,
  border: '1px solid var(--color-accent, #4c8dff)',
  background: 'var(--color-accent, #4c8dff)', color: '#fff',
}
